import type { components } from '@fluencelabs/vodopad-admin-api'
import type { FormInstance } from 'antd'
import {
  Alert,
  DatePicker,
  Form,
  Input,
  message,
  Modal,
  Select,
  Space,
  Switch,
  Typography,
} from 'antd'
import type { Dayjs } from 'dayjs'
import { useState } from 'react'

import { CurrentPriceInfo, useCurrentPrice } from './CurrentPriceInfo'
import type { PriceResourceType } from './priceUtils'
import {
  HOURS_PER_MONTH,
  postPriceUpdate,
  SECONDS_PER_HOUR,
  stripTrailingZeroes,
} from './priceUtils'
import { ResourceFields } from './ResourceFields'

export type { PriceResourceType } from './priceUtils'

type StorageTypeDto = components['schemas']['StorageTypeDto']
type IpAddressType = components['schemas']['IpAddressType']
type PriceUnit = 'per-second' | 'per-hour' | 'per-month'

interface FormValues {
  groupName: string
  resourceType: PriceResourceType
  clusterId: string
  priceValue: string
  activeSince: Dayjs
  activeUntil?: Dayjs | null
  vmConfigurationId?: string
  storageType?: StorageTypeDto
  replicated?: boolean
  addressType?: IpAddressType
}

export interface AddPricePrefill {
  groupName?: string
  resourceType?: PriceResourceType
  clusterId?: string
  vmConfigurationId?: string
  storageType?: StorageTypeDto
  replicated?: boolean
  addressType?: IpAddressType
}

function toPerSecond(value: number, unit: PriceUnit): number {
  if (unit === 'per-hour') return value / SECONDS_PER_HOUR
  if (unit === 'per-month') return value / HOURS_PER_MONTH / SECONDS_PER_HOUR
  return value
}

const PLACEHOLDER: Record<PriceUnit, string> = {
  'per-second': '0.000001',
  'per-hour': '0.0036',
  'per-month': '2.63',
}

async function submitPrice(
  values: FormValues,
  priceUnit: PriceUnit,
  expirePriceId?: string,
) {
  const pricePerSecondPerQty = String(
    toPerSecond(Number(values.priceValue), priceUnit),
  )
  const priceInfo = {
    pricePerSecondPerQty,
    activeSince: values.activeSince.toISOString(),
    activeUntil: values.activeUntil?.toISOString() ?? null,
  }
  const { clusterId, groupName, resourceType } = values
  const update = expirePriceId
    ? {
        [expirePriceId]: {
          activeUntil: values.activeSince.toISOString(),
        },
      }
    : undefined

  let typeId: Record<string, unknown>
  if (resourceType === 'vm') {
    typeId = {
      vmTypeId: {
        clusterId,
        userGroup: groupName,
        vmConfigurationId: values.vmConfigurationId ?? '',
      },
    }
  } else if (resourceType === 'storage') {
    typeId = {
      storageTypeId: {
        clusterId,
        userGroup: groupName,
        storageType: values.storageType ?? 'SSD',
        replicated: values.replicated ?? false,
      },
    }
  } else {
    typeId = {
      publicIpTypeId: {
        clusterId,
        userGroup: groupName,
        addressType: values.addressType ?? 'V4',
      },
    }
  }

  await postPriceUpdate(resourceType, {
    new: [{ priceInfo, ...typeId }],
    update,
  })
}

function ActiveUntilField({ form }: { form: FormInstance<FormValues> }) {
  const [hasActiveUntil, setHasActiveUntil] = useState(false)

  return (
    <Form.Item label="Active Until">
      <Space direction="vertical" style={{ width: '100%' }}>
        <Space size="small">
          <Switch
            checked={hasActiveUntil}
            onChange={(v) => {
              setHasActiveUntil(v)
              if (!v) form.setFieldValue('activeUntil', null)
            }}
          />
          <Typography.Text type="secondary">Set end date</Typography.Text>
        </Space>
        {hasActiveUntil && (
          <>
            <Alert
              type="warning"
              showIcon
              message="This price will expire at the specified date. Make sure a replacement price is scheduled."
            />
            <Form.Item
              name="activeUntil"
              noStyle
              rules={[
                {
                  required: true,
                  message: 'End date is required when enabled',
                },
              ]}
            >
              <DatePicker showTime style={{ width: '100%' }} />
            </Form.Item>
          </>
        )}
      </Space>
    </Form.Item>
  )
}

interface Props {
  open: boolean
  onClose: () => void
  onSuccess: () => void
  /** Pre-fill form fields from context */
  prefill?: AddPricePrefill
  /** User groups for the dropdown */
  groups: { name: string }[]
}

export function AddPriceModal({
  open,
  onClose,
  onSuccess,
  prefill,
  groups,
}: Props) {
  const [form] = Form.useForm<FormValues>()
  const [saving, setSaving] = useState(false)
  const [priceUnit, setPriceUnit] = useState<PriceUnit>('per-second')

  const watchPriceValue = Form.useWatch('priceValue', form) as
    | string
    | undefined
  const watchGroup = Form.useWatch('groupName', form)
  const watchResourceType = Form.useWatch('resourceType', form)
  const watchCluster = Form.useWatch('clusterId', form)
  const watchVmConfig = Form.useWatch('vmConfigurationId', form)
  const watchStorageType = Form.useWatch('storageType', form)
  const watchReplicated = Form.useWatch('replicated', form)
  const watchAddressType = Form.useWatch('addressType', form)

  const rt = (watchResourceType as PriceResourceType) ?? 'vm'

  const currentPrice = useCurrentPrice({
    resourceType: rt,
    groupName: (watchGroup as string) ?? '',
    clusterId: watchCluster as string,
    vmConfigurationId: watchVmConfig as string,
    storageType: watchStorageType as string,
    replicated: watchReplicated as boolean,
    addressType: watchAddressType as string,
  })

  const expirePriceId =
    currentPrice && !currentPrice.activeUntil ? currentPrice.id : undefined

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      setSaving(true)
      await submitPrice(values, priceUnit, expirePriceId)
      void message.success('Price added')
      form.resetFields()
      onSuccess()
    } catch (error) {
      if (error instanceof Error) {
        void message.error(error.message)
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      title="Add Price"
      open={open}
      onCancel={onClose}
      onOk={() => void handleSubmit()}
      okText="Add Price"
      confirmLoading={saving}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        preserve={false}
        initialValues={{
          replicated: false,
          resourceType: 'vm',
          ...prefill,
        }}
      >
        <Form.Item
          name="resourceType"
          label="Resource Type"
          rules={[{ required: true }]}
        >
          <Select>
            <Select.Option value="vm">VM</Select.Option>
            <Select.Option value="storage">Storage</Select.Option>
            <Select.Option value="public-ip">Public IP</Select.Option>
          </Select>
        </Form.Item>
        <Form.Item
          name="groupName"
          label="User Group"
          rules={[{ required: true }]}
        >
          <Select placeholder="Select group">
            {groups.map((g) => (
              <Select.Option key={g.name} value={g.name}>
                {g.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <ResourceFields resourceType={rt} clusterId={watchCluster as string} />
        <CurrentPriceInfo price={currentPrice} />
        <Form.Item
          name="priceValue"
          label="Price per qty"
          rules={[
            { required: true },
            {
              validator: (_, value: string) =>
                value && Number(value) > 0
                  ? Promise.resolve()
                  : Promise.reject(new Error('Must be a positive number')),
            },
          ]}
          help={(() => {
            const v = Number(watchPriceValue)
            if (!watchPriceValue || isNaN(v) || v <= 0) return undefined
            const perSec = toPerSecond(v, priceUnit)
            const perHour = perSec * SECONDS_PER_HOUR
            const perMonth = perHour * HOURS_PER_MONTH
            const s = stripTrailingZeroes(perSec.toPrecision(6))
            const h = stripTrailingZeroes(perHour.toPrecision(6))
            const m = stripTrailingZeroes(perMonth.toFixed(2))
            return `$${s}/s · $${h}/h · $${m}/mo`
          })()}
        >
          <Input
            placeholder={PLACEHOLDER[priceUnit]}
            addonAfter={
              <Select
                value={priceUnit}
                onChange={setPriceUnit}
                style={{ width: 120 }}
              >
                <Select.Option value="per-second">/ second</Select.Option>
                <Select.Option value="per-hour">/ hour</Select.Option>
                <Select.Option value="per-month">/ month</Select.Option>
              </Select>
            }
          />
        </Form.Item>
        <Form.Item
          name="activeSince"
          label="Active Since"
          rules={[{ required: true }]}
        >
          <DatePicker showTime style={{ width: '100%' }} />
        </Form.Item>
        <ActiveUntilField form={form} />
      </Form>
    </Modal>
  )
}
